// The shell's own screen part: realtime for the profile, favorites and visits,
// what cloud keeps across reloads, and the shell's shortcuts.

import type { Router } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import { datasetRealtime } from "../datasets/queries";
import { invalidateDraft } from "../me/draft";
import { invalidateFavorites } from "../me/favorites";
import { profileKey } from "../me/profile";
import { applyVisit, type Visit, visitsPrefix } from "../me/visits";
import { shellSettings } from "../settings/shell-settings";
import type { ShortcutDef } from "../shell/shortcuts";
import type { AppearanceRows } from "./context";
import { defineScreenPart, type ScreenPart } from "./screen-part";

export function shellPart(options: {
  toggleSheet(): void;
  // biome-ignore lint/suspicious/noExplicitAny: any router of the app
  router(): Router<any, any>;
  /** The host's rows at the end of Appearance (cloud: the home screen icon). */
  appearanceRows?: AppearanceRows;
  /** There may be an account (its name and picture lead General). */
  account?: boolean;
}): ScreenPart {
  const shortcuts: ShortcutDef[] = [
    {
      id: "shell.shortcuts",
      key: "?",
      group: "general",
      label: () => m.shell_shortcuts(),
      run: options.toggleSheet,
    },
    {
      // The old web's key for the sheet, kept for the hands used to it; the
      // sheet lists it on the same row as `?` (same label).
      id: "shell.shortcuts-mod",
      key: "mod+/",
      group: "general",
      label: () => m.shell_shortcuts(),
      run: options.toggleSheet,
    },
    {
      id: "shell.settings",
      key: "mod+,",
      group: "navigate",
      label: () => m.shell_settings(),
      run: () => void options.router().navigate({ to: "/me/settings" }),
    },
  ];
  return defineScreenPart({
    id: "shell",
    settings: shellSettings(options),
    shortcuts,
    realtime: {
      // Datasets are read by ui fences anywhere (documents, chats).
      ...datasetRealtime,
      "profile.changed": (_payload, { qc }) =>
        void qc.invalidateQueries({ queryKey: profileKey }),
      "favorite.changed": (_payload, { qc }) => void invalidateFavorites(qc),
      "draft.changed": (payload, { qc }) =>
        invalidateDraft(qc, (payload as { key?: string }).key),
      "visit.recorded": (payload, { qc }) => {
        const visit = payload as Partial<Visit>;
        if (visit.place && visit.kind && visit.targetId && visit.visitedAt)
          applyVisit(qc, visit as Visit);
        else void qc.invalidateQueries({ queryKey: visitsPrefix });
      },
    },
    persist: (key) =>
      key[0] === "me" &&
      (key[1] === "profile" || key[1] === "visits" || key[1] === "favorites"),
  });
}
